import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { fmtDate, today, fmtNum } from "@/lib/format";
import { Plus, Play, Pause, CheckCircle2, XCircle, Rocket, ChevronRight } from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";

const PRIORITIES = ["low", "normal", "high", "urgent"];
const STATUS_COLOURS = {
  draft: "text-muted-foreground",
  released: "text-blue-500",
  in_progress: "text-yellow-500",
  on_hold: "text-orange-500",
  completed: "text-emerald-600",
  closed: "text-emerald-700",
  cancelled: "text-red-500",
};

function WorkOrdersTab() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [routings, setRoutings] = useState([]);
  const [boms, setBoms] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailJv, setDetailJv] = useState([]);
  const empty = { item_id: "", qty_planned: 100, routing_id: "", bom_id: "",
                   priority: "normal", planned_start: today(), planned_end: today(),
                   remarks: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setRows((await api.get("/production/work-orders")).data);
    setItems((await api.get("/items")).data);
    setRoutings((await api.get("/mfg/routings")).data);
    setBoms((await api.get("/bom")).data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.item_id) return toast.error("Item is required");
    try {
      await api.post("/production/work-orders", {
        ...form,
        routing_id: form.routing_id || null,
        bom_id: form.bom_id || null,
      });
      toast.success("Work order created"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doAction = async (wo, action) => {
    try { await api.post(`/production/work-orders/${wo.id}/${action}`); toast.success(action); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openDetail = async (wo) => {
    const d = (await api.get(`/production/work-orders/${wo.id}`)).data;
    setDetail(d);
    // Iter-136 · Load linked journal entries for this WO (by reference)
    try {
      const v = await api.get("/vouchers", { params: { limit: 500 } });
      const all = v.data?.vouchers || v.data || [];
      setDetailJv(all.filter((x) => (x.reference || "").includes(wo.wo_number)));
    } catch (e) { setDetailJv([]); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => { setForm(empty); setOpen(true); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-wo-btn"><Plus className="w-3.5 h-3.5" /> Work Order</Button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>WO#</th><th>Date</th><th>Item</th><th className="text-right">Qty planned</th><th className="text-right">Produced</th><th>Progress</th><th>Priority</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-muted-foreground">No work orders yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} onClick={() => openDetail(r)} className="cursor-pointer hover:bg-muted/40" data-testid={`wo-row-${r.wo_number}`}>
                <td className="font-mono text-xs">{r.wo_number}</td>
                <td className="text-xs">{fmtDate(r.date)}</td>
                <td>{r.item_name}<div className="text-[10px] font-mono text-muted-foreground">{r.item_sku}</div></td>
                <td className="text-right tabular-nums">{fmtNum(r.qty_planned)}</td>
                <td className="text-right tabular-nums">{fmtNum(r.qty_produced)}</td>
                <td>
                  <div className="w-24 h-1.5 bg-muted rounded-sm overflow-hidden">
                    <div className="h-full bg-accent" style={{ width: `${Math.min(100, r.progress_pct)}%` }} />
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{r.progress_pct}%</div>
                </td>
                <td><span className={`text-[10px] font-mono uppercase ${r.priority === "urgent" ? "text-red-500" : r.priority === "high" ? "text-orange-500" : "text-muted-foreground"}`}>{r.priority}</span></td>
                <td><span className={`text-[10px] font-mono uppercase ${STATUS_COLOURS[r.status]}`}>{r.status.replace(/_/g, " ")}</span></td>
                <td className="text-right" onClick={(e) => e.stopPropagation()}>
                  {r.status === "draft" && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => doAction(r, "release")} data-testid={`release-wo-${r.wo_number}`}><Rocket className="w-3 h-3" /> Release</Button>}
                  {r.status === "completed" && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-emerald-600" onClick={() => doAction(r, "close")}>Close</Button>}
                  {!["closed", "cancelled"].includes(r.status) && <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => doAction(r, "cancel")}>Cancel</Button>}
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openDetail(r)}><ChevronRight className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create WO */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg rounded-none space-y-3 overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading">New Work Order</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Item (FG)</Label>
              <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="wo-item"><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent className="max-h-72"><div className="max-h-72 overflow-y-auto">{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} <span className="text-xs text-muted-foreground ml-2">{i.sku}</span></SelectItem>)}</div></SelectContent>
              </Select>
            </div>
            <div><Label>Qty planned</Label><Input type="number" value={form.qty_planned} onChange={(e) => setForm({ ...form, qty_planned: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" data-testid="wo-qty" /></div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>BOM (optional)</Label>
              <Select value={form.bom_id} onValueChange={(v) => setForm({ ...form, bom_id: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="(auto)" /></SelectTrigger>
                <SelectContent>{boms.filter((b) => !form.item_id || b.parent_item_id === form.item_id).map((b) => <SelectItem key={b.id} value={b.id}>{b.code || b.id.slice(0, 6)} · {b.parent_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Routing</Label>
              <Select value={form.routing_id} onValueChange={(v) => setForm({ ...form, routing_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="wo-routing"><SelectValue placeholder="Select routing" /></SelectTrigger>
                <SelectContent>{routings.map((r) => <SelectItem key={r.id} value={r.id}>{r.code} · {r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Planned start</Label><Input type="date" value={form.planned_start} onChange={(e) => setForm({ ...form, planned_start: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>Planned end</Label><Input type="date" value={form.planned_end} onChange={(e) => setForm({ ...form, planned_end: e.target.value })} className="rounded-sm mt-1" /></div>
            <div className="col-span-2"><Label>Remarks</Label><Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="rounded-sm mt-1" /></div>
          </div>
          <Button onClick={create} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="wo-save">Save as draft</Button>
        </SheetContent>
      </Sheet>

      {/* Detail */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none space-y-3 overflow-y-auto">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-heading">{detail.wo_number} · {items.find((i) => i.id === detail.item_id)?.name}</SheetTitle></SheetHeader>
              <div className="grid grid-cols-4 gap-3">
                <div className="card-sharp p-2"><div className="text-[10px] font-mono uppercase text-muted-foreground">Planned</div><div className="text-lg font-medium tabular-nums">{fmtNum(detail.qty_planned)}</div></div>
                <div className="card-sharp p-2"><div className="text-[10px] font-mono uppercase text-emerald-600">Produced</div><div className="text-lg font-medium tabular-nums">{fmtNum(detail.qty_produced)}</div></div>
                <div className="card-sharp p-2"><div className="text-[10px] font-mono uppercase text-red-500">Rejected</div><div className="text-lg font-medium tabular-nums">{fmtNum(detail.qty_rejected)}</div></div>
                <div className="card-sharp p-2"><div className="text-[10px] font-mono uppercase text-accent">Status</div><div className="text-sm font-medium">{detail.status}</div></div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Job Cards ({detail.job_cards?.length || 0})</div>
                <div className="space-y-2">
                  {(detail.job_cards || []).map((jc) => (
                    <div key={jc.id} className="border rounded-sm p-2 flex items-center gap-3">
                      <span className="font-mono text-[10px] text-accent">{jc.op_seq}</span>
                      <div className="flex-1">
                        <div className="text-sm font-medium">{jc.operation_name}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">{jc.jc_number} · {jc.machine_code || "no machine"}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs tabular-nums">{fmtNum(jc.qty_good)} / {fmtNum(jc.qty_planned)}</div>
                        <div className={`text-[10px] font-mono uppercase ${jc.status === "completed" ? "text-emerald-600" : jc.status === "running" ? "text-yellow-500" : "text-muted-foreground"}`}>{jc.status}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {detailJv.length > 0 && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-accent mt-4 mb-2">// Linked journal entries ({detailJv.length})</div>
                  <div className="border rounded-sm overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/40 text-[10px] font-mono uppercase text-muted-foreground"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Voucher</th><th className="p-2 text-left">Narration</th><th className="p-2 text-right">DR</th></tr></thead>
                      <tbody>
                        {detailJv.map((v) => (
                          <tr key={v.id} className="border-t"><td className="p-2 font-mono">{v.date}</td><td className="p-2 font-mono text-accent">{v.voucher_number}</td><td className="p-2 text-muted-foreground truncate max-w-sm">{v.narration}</td><td className="p-2 text-right tabular-nums font-medium">{fmtNum(v.total_debit)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function JobCardsTab() {
  const [rows, setRows] = useState([]);
  const [wos,  setWos]  = useState({});
  const [items, setItems] = useState({});
  const [view, setView] = useState(null);

  const load = async () => {
    try {
      const jc = (await api.get("/production/job-cards")).data;
      const wo = (await api.get("/production/work-orders")).data;
      const it = (await api.get("/items")).data;
      setRows(jc);
      setWos(Object.fromEntries(wo.map(w => [w.id, w])));
      setItems(Object.fromEntries(it.map(i => [i.id, i])));
    } catch (e) { toast.error("Failed to load job cards"); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3">
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>JC#</th><th>WO / Item</th><th>Operation</th><th className="text-right">Planned</th><th className="text-right">Good</th><th className="text-right">Reject</th><th>Started</th><th>Ended</th><th>Status</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-muted-foreground">No job cards yet — release a Work Order to generate them.</td></tr>}
            {rows.map((jc) => {
              const wo = wos[jc.work_order_id];
              const it = wo && items[wo.item_id];
              return (
                <tr key={jc.id} onClick={() => setView({ jc, wo, it })} className="cursor-pointer hover:bg-muted/40" data-testid={`jc-row-${jc.jc_number}`}>
                  <td className="font-mono text-xs">{jc.jc_number}</td>
                  <td>
                    <div className="font-mono text-xs">{wo?.wo_number || "—"}</div>
                    <div className="text-[10px] text-muted-foreground">{it?.name || ""}</div>
                  </td>
                  <td className="text-xs">
                    <div className="font-mono text-[10px] text-accent">op-{jc.op_seq}</div>
                    <div>{jc.operation_name || "—"}</div>
                  </td>
                  <td className="text-right tabular-nums">{fmtNum(jc.qty_planned)}</td>
                  <td className="text-right tabular-nums text-emerald-600">{fmtNum(jc.qty_good)}</td>
                  <td className="text-right tabular-nums text-red-600">{fmtNum(jc.qty_rejected)}</td>
                  <td className="text-xs font-mono">{(jc.run_started_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="text-xs font-mono">{(jc.run_ended_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td><span className={`text-[10px] font-mono uppercase ${jc.status === "completed" ? "text-emerald-600" : jc.status === "running" ? "text-yellow-500" : "text-muted-foreground"}`}>{jc.status}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Sheet open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl rounded-none space-y-4 overflow-y-auto" data-testid="jc-view-drawer">
          <SheetHeader className="pb-3 border-b">
            <SheetTitle className="font-heading">
              Job Card {view?.jc?.jc_number}
              <div className="text-xs text-muted-foreground mt-1 font-normal">
                {view?.wo?.wo_number} · {view?.it?.name || "—"}
              </div>
            </SheetTitle>
          </SheetHeader>
          {view?.jc && (
            <div className="text-sm space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="card-sharp p-3">
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">Operation</div>
                  <div className="mt-1 text-sm">{view.jc.operation_name || view.jc.operation_code || "—"}</div>
                  <div className="text-[10px] font-mono text-muted-foreground mt-0.5">Seq: {view.jc.op_seq}</div>
                </div>
                <div className="card-sharp p-3">
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">Machine</div>
                  <div className="mt-1 text-sm font-mono">{view.jc.machine_code || view.jc.machine_id?.slice(0, 8) || "—"}</div>
                </div>
                <div className="card-sharp p-3">
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">Started</div>
                  <div className="mt-1 text-sm font-mono">{(view.jc.run_started_at || "").slice(0, 16).replace("T", " ") || "—"}</div>
                </div>
                <div className="card-sharp p-3">
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">Ended</div>
                  <div className="mt-1 text-sm font-mono">{(view.jc.run_ended_at || "").slice(0, 16).replace("T", " ") || "—"}</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Planned</div><div className="text-lg font-bold tabular-nums mt-1">{fmtNum(view.jc.qty_planned)}</div></div>
                <div className="card-sharp p-3 bg-emerald-500/5"><div className="text-[10px] font-mono uppercase text-emerald-600">Good</div><div className="text-lg font-bold tabular-nums mt-1">{fmtNum(view.jc.qty_good)}</div></div>
                <div className="card-sharp p-3 bg-red-500/5"><div className="text-[10px] font-mono uppercase text-red-600">Rejected</div><div className="text-lg font-bold tabular-nums mt-1">{fmtNum(view.jc.qty_rejected)}</div></div>
                <div className="card-sharp p-3 bg-amber-500/5"><div className="text-[10px] font-mono uppercase text-amber-600">Rework</div><div className="text-lg font-bold tabular-nums mt-1">{fmtNum(view.jc.qty_rework || 0)}</div></div>
              </div>
              <div className="card-sharp p-3">
                <div className="text-[10px] font-mono uppercase text-muted-foreground">Status</div>
                <div className={`mt-1 text-sm font-mono uppercase ${view.jc.status === "completed" ? "text-emerald-600" : view.jc.status === "running" ? "text-yellow-500" : "text-muted-foreground"}`}>{view.jc.status}</div>
              </div>
              {view.wo && (
                <div className="border rounded-sm p-3 bg-accent/5">
                  <div className="text-[10px] font-mono uppercase text-accent">// Parent Work Order</div>
                  <div className="mt-1 font-mono text-sm">{view.wo.wo_number}</div>
                  <div className="text-xs text-muted-foreground">Qty: {fmtNum(view.wo.qty_produced)} / {fmtNum(view.wo.qty_planned)} · Status: <span className="font-mono uppercase">{view.wo.status}</span></div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// Iter-136 · Manufacturing Journal Entries — filter vouchers to mfg-related sources
function MfgJournalTab() {
  const [vouchers, setVouchers] = useState([]);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/vouchers", { params: { limit: 500 } });
        const all = r.data?.vouchers || r.data || [];
        // Mfg vouchers: source_type or narration hints at Manufacturing / WIP / WO / BOM
        const MFG_SOURCES = ["work_order", "work_order_complete", "work_order_scrap",
                              "bom_issue", "bom_receive", "stock_transfer", "job_work_bill"];
        const filtered = all.filter((v) => {
          if (MFG_SOURCES.includes(v.source_type)) return true;
          const nar = (v.narration || "").toLowerCase();
          const ref = (v.reference || "").toUpperCase();
          return /wip|work.?order|bom|manufacturing|job.?work|scrap|shop.?floor/.test(nar) ||
                 /^WO\/|^JC\/|^JW-|^BOMI-|^BOMR-/.test(ref);
        });
        setVouchers(filtered);
      } catch (e) { toast.error("Failed to load"); }
      setBusy(false);
    })();
  }, []);

  const bySource = vouchers.reduce((acc, v) => {
    const key = v.source_type || "manual_jv";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const totalDR = vouchers.reduce((s, v) => s + Number(v.total_debit || 0), 0);

  return (
    <div className="space-y-3">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="card-sharp p-3 bg-accent/5"><div className="text-[10px] font-mono uppercase text-accent">Total Mfg Vouchers</div><div className="mt-1 text-2xl font-bold tabular-nums">{vouchers.length}</div></div>
        <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Value posted</div><div className="mt-1 text-lg font-bold tabular-nums">{fmtNum(totalDR / 100000)}L</div></div>
        <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Work Order</div><div className="mt-1 text-lg tabular-nums">{(bySource.work_order || 0) + (bySource.work_order_complete || 0)}</div></div>
        <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Job Work</div><div className="mt-1 text-lg tabular-nums">{bySource.job_work_bill || 0}</div></div>
        <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Scrap / Stock</div><div className="mt-1 text-lg tabular-nums">{(bySource.work_order_scrap || 0) + (bySource.stock_transfer || 0)}</div></div>
      </div>

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Date</th><th>Voucher #</th><th>Type</th><th>Source</th><th>Narration</th><th>Reference</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
          <tbody>
            {busy && <tr><td colSpan="8" className="text-center py-8 text-muted-foreground">Loading…</td></tr>}
            {!busy && vouchers.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-muted-foreground">No manufacturing journal entries yet. Post a Work Order + BOM Issue / Receive to see them here.</td></tr>}
            {vouchers.map((v) => (
              <tr key={v.id} className="cursor-pointer hover:bg-muted/40" onClick={() => setView(v)} data-testid={`mfg-v-${v.voucher_number}`}>
                <td className="text-xs font-mono whitespace-nowrap">{v.date}</td>
                <td className="font-mono text-xs">{v.voucher_number}</td>
                <td><span className="text-[10px] font-mono uppercase text-accent">{v.voucher_type}</span></td>
                <td><span className="text-[10px] font-mono uppercase text-emerald-600">{(v.source_type || "manual").replace(/_/g, " ")}</span></td>
                <td className="text-xs text-muted-foreground max-w-xs truncate">{v.narration || "—"}</td>
                <td className="text-xs font-mono text-muted-foreground">{v.reference || "—"}</td>
                <td className="text-right tabular-nums">{fmtNum(v.total_debit)}</td>
                <td className="text-right tabular-nums">{fmtNum(v.total_credit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <SheetContent side="right" className="w-full sm:max-w-3xl rounded-none space-y-4 overflow-y-auto" data-testid="mfg-v-drawer">
          <SheetHeader className="pb-3 border-b">
            <SheetTitle className="font-heading flex items-center gap-3">
              <span className="text-[10px] font-mono uppercase text-accent px-2 py-0.5 rounded-sm bg-accent/10">{view?.voucher_type}</span>
              {view?.voucher_number}
              <span className="text-[10px] font-mono text-muted-foreground ml-auto">{view?.date}</span>
            </SheetTitle>
          </SheetHeader>
          {view && (
            <div className="text-sm space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Source</div><div className="mt-1 font-mono uppercase text-xs text-emerald-600">{(view.source_type || "manual").replace(/_/g, " ")}</div></div>
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Reference</div><div className="mt-1 font-mono text-xs">{view.reference || "—"}</div></div>
              </div>
              {view.narration && (
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Narration</div><div className="mt-1 text-xs">{view.narration}</div></div>
              )}
              <div>
                <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2">// Account lines</div>
                <div className="border rounded-sm overflow-hidden"><table className="w-full text-xs">
                  <thead className="bg-muted/40 text-[10px] font-mono uppercase text-muted-foreground"><tr><th className="p-2 text-left">Account</th><th className="p-2 text-left">Narration</th><th className="p-2 text-right">Debit</th><th className="p-2 text-right">Credit</th></tr></thead>
                  <tbody>
                    {(view.lines || []).map((l, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2 font-mono text-[10px]">{l.account_code} · {l.account_name}</td>
                        <td className="p-2 text-[10px] text-muted-foreground">{l.narration || "—"}</td>
                        <td className="p-2 text-right tabular-nums">{Number(l.debit || 0) > 0 ? fmtNum(l.debit) : ""}</td>
                        <td className="p-2 text-right tabular-nums">{Number(l.credit || 0) > 0 ? fmtNum(l.credit) : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-muted/20 font-medium">
                    <tr><td className="p-2 text-right" colSpan="2">Total</td><td className="p-2 text-right tabular-nums">{fmtNum(view.total_debit)}</td><td className="p-2 text-right tabular-nums">{fmtNum(view.total_credit)}</td></tr>
                  </tfoot>
                </table></div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function LabourRatesTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = { code: "", role: "Operator", grade: "L1", hourly_rate: 0, ot_multiplier: 1.5, night_multiplier: 1.15, is_active: true };
  const [form, setForm] = useState(empty);

  const load = async () => setRows((await api.get("/mfg/labour-rates")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.code || !form.role) return toast.error("Code + Role required");
    try {
      if (edit) await api.put(`/mfg/labour-rates/${edit.id}`, form);
      else await api.post("/mfg/labour-rates", form);
      toast.success("Saved"); setOpen(false); setEdit(null); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/mfg/labour-rates/${id}`); load(); };

  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button onClick={() => { setEdit(null); setForm(empty); setOpen(true); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-labour-btn"><Plus className="w-3.5 h-3.5" /> Labour rate</Button></div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Code</th><th>Role</th><th>Grade</th><th className="text-right">Base ₹/hr</th><th className="text-right">OT ×</th><th className="text-right">Night ×</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-muted-foreground">No labour rates yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`labour-row-${r.code}`}>
                <td className="font-mono text-xs">{r.code}</td>
                <td>{r.role}</td>
                <td><span className="text-[10px] font-mono uppercase text-accent">{r.grade || "—"}</span></td>
                <td className="text-right tabular-nums">₹{r.hourly_rate}</td>
                <td className="text-right tabular-nums">{r.ot_multiplier}</td>
                <td className="text-right tabular-nums">{r.night_multiplier}</td>
                <td className="text-right">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setEdit(r); setForm({ ...r }); setOpen(true); }}>Edit</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => del(r.id)}>Del</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader><SheetTitle className="font-heading">{edit ? "Edit" : "New"} Labour Rate</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" data-testid="labour-code" /></div>
            <div><Label>Role</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="rounded-sm mt-1" data-testid="labour-role" /></div>
            <div><Label>Grade</Label><Input value={form.grade || ""} onChange={(e) => setForm({ ...form, grade: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>Base ₹/hr</Label><Input type="number" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" data-testid="labour-rate" /></div>
            <div><Label>OT ×</Label><Input type="number" step="0.1" value={form.ot_multiplier} onChange={(e) => setForm({ ...form, ot_multiplier: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>Night ×</Label><Input type="number" step="0.1" value={form.night_multiplier} onChange={(e) => setForm({ ...form, night_multiplier: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
          </div>
          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="labour-save">Save</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function OEETab() {
  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); });
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const load = async () => setData((await api.get("/production/oee/summary", { params: { from_date: from, to_date: to } })).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const gradeColor = (v) => v >= 85 ? "text-emerald-600" : v >= 60 ? "text-yellow-500" : "text-red-500";
  return (
    <div className="space-y-4" data-testid="oee-tab">
      <div className="flex items-end gap-3">
        <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm w-40" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm w-40" /></div>
        <Button onClick={load} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="oee-run">Compute</Button>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-4 gap-3">
            <div className="card-sharp p-4"><div className="text-[10px] font-mono uppercase text-muted-foreground">Avg OEE</div><div className={`text-3xl font-medium tabular-nums ${gradeColor(data.avg_oee)}`} data-testid="oee-avg">{data.avg_oee}%</div><div className="text-[10px] font-mono text-muted-foreground mt-1">world-class ≥ 85%</div></div>
            <div className="card-sharp p-4"><div className="text-[10px] font-mono uppercase text-blue-500">Availability</div><div className="text-3xl font-medium tabular-nums">{data.avg_availability}%</div></div>
            <div className="card-sharp p-4"><div className="text-[10px] font-mono uppercase text-yellow-500">Performance</div><div className="text-3xl font-medium tabular-nums">{data.avg_performance}%</div></div>
            <div className="card-sharp p-4"><div className="text-[10px] font-mono uppercase text-emerald-600">Quality</div><div className="text-3xl font-medium tabular-nums">{data.avg_quality}%</div></div>
          </div>
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead><tr><th>Machine</th><th className="text-right">Planned (min)</th><th className="text-right">Downtime</th><th className="text-right">Run</th><th className="text-right">Units</th><th className="text-right">Good</th><th className="text-right">A</th><th className="text-right">P</th><th className="text-right">Q</th><th className="text-right">OEE</th></tr></thead>
              <tbody>
                {data.machines.length === 0 && <tr><td colSpan="10" className="text-center py-10 text-muted-foreground">No machine activity in this window.</td></tr>}
                {data.machines.map((m) => (
                  <tr key={m.machine_id}>
                    <td className="font-mono text-xs">{m.machine_code}<div className="text-[10px] font-mono text-muted-foreground">{m.machine_name}</div></td>
                    <td className="text-right tabular-nums text-xs">{m.planned_min}</td>
                    <td className="text-right tabular-nums text-xs">{m.downtime_min}</td>
                    <td className="text-right tabular-nums text-xs">{m.run_min}</td>
                    <td className="text-right tabular-nums text-xs">{m.total_units}</td>
                    <td className="text-right tabular-nums text-xs">{m.good_units}</td>
                    <td className="text-right tabular-nums text-xs">{m.availability}%</td>
                    <td className="text-right tabular-nums text-xs">{m.performance}%</td>
                    <td className="text-right tabular-nums text-xs">{m.quality}%</td>
                    <td className={`text-right tabular-nums font-medium ${gradeColor(m.oee)}`}>{m.oee}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default function Production() {
  const { hasModule } = useSubscription();
  if (!hasModule("production")) {
    return (
      <div className="p-8" data-testid="production-locked">
        <div className="card-sharp p-10 text-center">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Enterprise Manufacturing</div>
          <div className="font-heading text-2xl mt-3">Production, OEE & Shop-Floor require Enterprise Manufacturing</div>
          <a href="/app/subscription" className="inline-block mt-5 px-5 py-2 bg-accent text-white rounded-sm text-sm font-medium">Upgrade →</a>
        </div>
      </div>
    );
  }
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="production-page">
      <PageHeader eyebrow="Manufacturing" title="Production"
        description="Work orders → job cards → OEE. Release drops job cards to the shop-floor kiosk automatically." />
      <Tabs defaultValue="wo">
        <TabsList className="rounded-sm">
          <TabsTrigger value="wo" className="rounded-sm" data-testid="tab-wo">Work Orders</TabsTrigger>
          <TabsTrigger value="jc" className="rounded-sm" data-testid="tab-jc">Job Cards</TabsTrigger>
          <TabsTrigger value="mfg-journal" className="rounded-sm" data-testid="tab-mfg-journal">Journal Entries</TabsTrigger>
          <TabsTrigger value="labour" className="rounded-sm" data-testid="tab-labour">Labour Rates</TabsTrigger>
          <TabsTrigger value="oee" className="rounded-sm" data-testid="tab-oee">OEE</TabsTrigger>
        </TabsList>
        <TabsContent value="wo" className="mt-4"><WorkOrdersTab /></TabsContent>
        <TabsContent value="jc" className="mt-4"><JobCardsTab /></TabsContent>
        <TabsContent value="mfg-journal" className="mt-4"><MfgJournalTab /></TabsContent>
        <TabsContent value="labour" className="mt-4"><LabourRatesTab /></TabsContent>
        <TabsContent value="oee" className="mt-4"><OEETab /></TabsContent>
      </Tabs>
    </div>
  );
}
